import { format as formatYmd } from 'date-fns';
import { fork, put, take, takeEvery } from 'redux-saga/effects';

import {
  KiSyokenKasidamaSearchParams,
  KiSyokenSearchParams,
} from '../domain/kiSyoken/types';
import { mergeYmdListsForWeekdayHolidaySeparation } from '../domain/kiSyoken/weekdayHolidayDate';

import {
  DataKiSyokenKasidamaActionCreators,
  DataKiSyokenKasidamaActionTypes,
  dataKiSyokenKasidamaSettingSelector,
} from '../redux/server/dataKiSyokenKasidama';
import { dataColumnsSwappedSelector } from '../redux/server/dataKiSyokenKasidamaColumnsSwappedData';
import {
  SettingsNationalHolidaysActionCreators,
  SettingsNationalHolidaysActionTypes,
  settingsNationalHolidaysSelector,
} from '../redux/server/settingsNationalHolidays';
import { settingsOptionsKiSyokenKasidamaFieldsSelector } from '../redux/server/settingsOptionsKiSyoken';
import {
  ChangeKiSyokenKasidamaFieldsAction,
  ChangeKiSyokenKasidamaShuAction,
  ChangeKiSyokenKasidamaSortAction,
  KiSyokenSettingActionCreators,
  KiSyokenSettingActionTypes,
  SearchKiSyokenKasidamaAction,
  TriggerKiSyokenKasidamaSwapFieldsAction,
  kiSyokenKasidamaColumnsOrderSelector,
  kiSyokenKasidamaSearchParamsSelector,
  kiSyokenSearchParamsSelector,
} from '../redux/ui/kiSyokenSettings';
import { kiSyokenSelectedDateRangeParamsSelector } from '../redux/ui/kiSyokenSettings';
import { customSelect } from '../utils/customSelect';
import { excludeHoliday } from '../utils/excludeHoliday';
import { excludeWeekday } from '../utils/excludeWeekday';
import { buildColumnsOrderByPatternOrder } from '../utils/fieldsOrder';
import { formatWeekdayAndHoliday } from '../utils/formatWeekdayAndHoliday';
import { recalcColumnOrder } from '../utils/orderedCell';
import { selectShu2SearchCondition } from '../utils/shu';

// ymd 系のみを残す（start/end 系は除去）
function keepOnlyYmd(
  params: KiSyokenKasidamaSearchParams
): KiSyokenKasidamaSearchParams {
  const {
    startDate: _start,
    endDate: _end,
    startComparisonDate: _startComparison,
    endComparisonDate: _endComparison,
    ...onlyYmd
  } = params;
  return onlyYmd;
}

// 平日/土日祝比較用：ymd系を残し、比較期間のstart/endも保持
function keepYmdWithComparison(
  params: KiSyokenKasidamaSearchParams
): KiSyokenKasidamaSearchParams {
  const { startDate: _start, endDate: _end, ...ymdWithComparison } = params;
  return ymdWithComparison;
}

// start/end 系のみを残す（ymd 系は除去）
function keepOnlyStartEnd(
  params: KiSyokenKasidamaSearchParams
): KiSyokenKasidamaSearchParams {
  const {
    ymdList: _ymdList,
    ymdComparisonList: _ymdComparisonList,
    ...onlyStartEnd
  } = params;
  return onlyStartEnd;
}

// 未分離の ymdList を解決する（優先度: メイン → 引数 → start/end 生成）
function* resolveUnseparatedYmdListForKasidama(
  baseParams: KiSyokenKasidamaSearchParams
) {
  const mainSearchParams = yield* customSelect(kiSyokenSearchParamsSelector);

  const mainYmdList = mainSearchParams?.ymdList ?? [];
  const mainYmdComparisonList = mainSearchParams?.ymdComparisonList ?? [];

  if (mainYmdList.length > 0 || mainYmdComparisonList.length > 0) {
    return mergeYmdListsForWeekdayHolidaySeparation(
      mainYmdList,
      mainYmdComparisonList
    );
  }

  const baseYmdList = baseParams.ymdList ?? [];
  const baseYmdComparisonList = baseParams.ymdComparisonList ?? [];
  if (baseYmdList.length > 0 || baseYmdComparisonList.length > 0) {
    return mergeYmdListsForWeekdayHolidaySeparation(
      baseYmdList,
      baseYmdComparisonList
    );
  }

  // 引数のstartDateとendDateが存在する場合はそれを使用する
  if (baseParams.startDate && baseParams.endDate) {
    const start = new Date(baseParams.startDate);
    const end = new Date(baseParams.endDate);
    const range: Date[] = [];
    for (
      let d = new Date(start.getTime());
      d.getTime() <= end.getTime();
      d.setDate(d.getDate() + 1)
    ) {
      range.push(new Date(d.getTime()));
    }
    const generated = range.map((d) => formatYmd(d, 'yyyy-MM-dd'));
    return generated;
  }
  return undefined;
}

// 共通：Kasidama用 API パラメータのビルド（ルール適用）
function* buildKasidamaParamsForApi(params: KiSyokenKasidamaSearchParams) {
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );

  if (selectedDateRangeParams.comparisonSection === '平日/土日祝比較') {
    const separated = yield* buildSeparatedKasidamaParams(params);
    const ymdWithComparison = keepYmdWithComparison(separated);
    return ymdWithComparison;
  }
  if (selectedDateRangeParams.dateUnit === '自由選択') {
    const onlyYmd = keepOnlyYmd(params);

    return onlyYmd;
  }
  const onlyStartEnd = keepOnlyStartEnd(params);
  return onlyStartEnd;
}

/**
 * テーブルデータfetch用のラッパー関数。fetchしかつsearchParamsに値を設定する
 */
function* fetchDataKiSyokenKasidamaSaga(
  searchParams: KiSyokenKasidamaSearchParams
) {
  yield put(
    DataKiSyokenKasidamaActionCreators.fetchDataKiSyokenKasidamaAction(
      searchParams
    )
  );
  yield take(
    DataKiSyokenKasidamaActionTypes.FETCH_DATA_KISYOKEN_KASIDAMA_SUCCESS
  );
  const setting = yield* customSelect(dataKiSyokenKasidamaSettingSelector);
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenKasidamaSearchParamsAction(
      setting ?? {}
    )
  );
}

/**
 * 平日/土日祝比較用に、未分離のymdListから平日と土日祝を分離し、
 * API送信用のパラメータに整形する（start/end 系は送らない）
 */
function* buildSeparatedKasidamaParams(
  baseParams: KiSyokenKasidamaSearchParams
) {
  const holidaysState = yield* customSelect(settingsNationalHolidaysSelector);
  if (!holidaysState) {
    yield put(
      SettingsNationalHolidaysActionCreators.fetchSettingsNationalHolidaysAction()
    );
    yield take(
      SettingsNationalHolidaysActionTypes.FETCH_SETTINGS_NATIONAL_HOLIDAYS_SUCCESS
    );
  }

  // 分離前に更新されたかもしれないので再度取得
  const updatedHolidays = yield* customSelect(settingsNationalHolidaysSelector);

  // 祝日情報を取得
  const holidays = updatedHolidays?.holidays ?? [];

  // 既存のymdComparisonListにはUI用の未分離値が入っている可能性があるため、常に未分離リストから再分離する

  // 元の（未分離の）ymdList候補を、メイン検索条件から優先的に取得
  const unseparatedList: string[] | undefined =
    yield* resolveUnseparatedYmdListForKasidama(baseParams);

  const originalYmdList = (unseparatedList ?? [])
    .map((v: string) => new Date(v))
    .filter((d) => !Number.isNaN(d.getTime()));

  // 平日/土日祝比較用に分離
  const formatted = formatWeekdayAndHoliday({
    ymdList: excludeHoliday(originalYmdList, holidays).map((d: Date) =>
      formatYmd(d, 'yyyy-MM-dd')
    ),
    ymdComparisonList: excludeWeekday(originalYmdList, holidays).map(
      (d: Date) => formatYmd(d, 'yyyy-MM-dd')
    ),
  });
  // start/end 系は送らず、ymd 系のみを返す
  const {
    startDate: _start,
    endDate: _end,
    startComparisonDate: _startComparison,
    endComparisonDate: _endComparison,
    ...restNonDateParams
  } = baseParams;

  // kasidamaの型定義ではnullが許可されていないため、nullの場合はundefinedに変換
  const {
    startDate,
    endDate,
    startComparisonDate,
    endComparisonDate,
    ymdList,
    ymdComparisonList,
    ...dateParams
  } = formatted;

  // ymdComparisonListが空の場合は比較期間のstart/endをnullにする
  const hasComparisonData = ymdComparisonList && ymdComparisonList.length > 0;

  return {
    ...restNonDateParams,
    ymdList,
    ymdComparisonList: hasComparisonData ? ymdComparisonList : undefined,
    ...dateParams,
    ...(startDate !== null && startDate !== undefined && { startDate }),
    ...(endDate !== null && endDate !== undefined && { endDate }),
    ...(hasComparisonData
      ? { startComparisonDate }
      : { startComparisonDate: null }),
    ...(hasComparisonData
      ? { endComparisonDate }
      : { endComparisonDate: null }),
  };
}

/**
 * メインテーブルの検索条件を元にチェーン店一覧の検索条件を生成する
 *
 * 種別はデフォルトで「種別全体」とするためshuList,shuGroupIdsは指定しない
 */
function generateKasidamaSearchParams(
  kiCode: string,
  searchParams: KiSyokenSearchParams,
  kasidamaSearchParams: KiSyokenKasidamaSearchParams
): KiSyokenKasidamaSearchParams {
  return {
    // fields、sort、orderは前回の情報を引き継ぐ
    fields: kasidamaSearchParams.fields,
    sort: kasidamaSearchParams.sort,
    order: kasidamaSearchParams.order,
    // それ以外は初回はメインテーブルの情報を利用する
    days: kasidamaSearchParams.days ?? searchParams.days,
    dayType: kasidamaSearchParams.dayType ?? searchParams.dayType,
    dateSuffixes:
      kasidamaSearchParams.dateSuffixes ?? searchParams.dateSuffixes,
    dates: kasidamaSearchParams.dates ?? searchParams.dates,
    makers: kasidamaSearchParams.makers ?? searchParams.makers,
    makersForSijiritu:
      kasidamaSearchParams.makersForSijiritu ?? searchParams.makersForSijiritu,
    sisTypes: kasidamaSearchParams.sisTypes ?? searchParams.sisTypes,
    sisTypesForSijiritu:
      kasidamaSearchParams.sisTypesForSijiritu ??
      searchParams.sisTypesForSijiritu,
    // 機種は毎回指定する
    kiList: [kiCode],
    kiListForSijiritu:
      kasidamaSearchParams.kiListForSijiritu ?? searchParams.kiListForSijiritu,
    ...(searchParams.startDate && {
      startDate: searchParams.startDate,
    }),
    ...(searchParams.endDate && {
      endDate: searchParams.endDate,
    }),
    ...(searchParams.startComparisonDate && {
      startComparisonDate: searchParams.startComparisonDate,
    }),
    ...(searchParams.endComparisonDate && {
      endComparisonDate: searchParams.endComparisonDate,
    }),
    ...(searchParams.ymdList && {
      ymdList: searchParams.ymdList,
    }),
    ...(searchParams.ymdComparisonList && {
      ymdComparisonList: searchParams.ymdComparisonList,
    }),
    excludeToday:
      kasidamaSearchParams.excludeToday ?? searchParams.excludeToday,

    // TODO: SIS対応前に以下を実装していても問題ないが念の為SIS対応後にコメントアウトを外す。

    // SISが実装されるまではバックエンドデフォルト値が存在しないためパラメータに付与されない。
    // sisDateRangeType:
    //   kasidamaSearchParams.sisDateRangeType ?? searchParams.sisDateRangeType,
    // sisStartDate:
    //   kasidamaSearchParams.sisStartDate ?? searchParams.sisStartDate,
    // sisEndDate: kasidamaSearchParams.sisEndDate ?? searchParams.sisEndDate,
    // sisFieldTypes:
    //   kasidamaSearchParams.sisFieldTypes ?? searchParams.sisFieldTypes,
    // sisBreakEvenForPachinko:
    //   kasidamaSearchParams.sisBreakEvenForPachinko ??
    //   searchParams.sisBreakEvenForPachinko,
    // sisBreakEvenForPachislo:
    //   kasidamaSearchParams.sisBreakEvenForPachislo ??
    //   searchParams.sisBreakEvenForPachislo,
    // sisRank: kasidamaSearchParams.sisRank ?? searchParams.sisRank,
    // sisArea: kasidamaSearchParams.sisArea ?? searchParams.sisArea,

    // containsSisAverageはKiSyokenSearchParamsには存在しない。
    // お気に入り保存後にバックエンドデフォルト値がfalseからtrueに切り替わった時に対応できるようにfalseの場合はパラメータを付与しない様にしている。
    // ...(kasidamaSearchParams.containsSisAverage && {
    //   containsSisAverage: kasidamaSearchParams.containsSisAverage,
    // }),
  };
}

/**
 * メインテーブルからの機種選択(初回取得含む)、機種セレクタで機種変更時のデータ取得
 */
function* searchKiSyokenKasidamaDataSaga(action: SearchKiSyokenKasidamaAction) {
  const { kiCode } = action.payload;
  const kiSyokenSearchParams = yield* customSelect(
    kiSyokenSearchParamsSelector
  );
  const kiSyokenKasidamaSearchParams = yield* customSelect(
    kiSyokenKasidamaSearchParamsSelector
  );

  const generatedSearchParams = generateKasidamaSearchParams(
    kiCode,
    kiSyokenSearchParams,
    kiSyokenKasidamaSearchParams
  );

  // 共通ヘルパでAPI送信用パラメータを構築
  const finalParams = yield* buildKasidamaParamsForApi(generatedSearchParams);
  yield fork(fetchDataKiSyokenKasidamaSaga, finalParams);
}

/**
 * 種別変更時のデータ取得
 */
function* changeKiSyokenKasidamaShuSagas(
  action: ChangeKiSyokenKasidamaShuAction
) {
  // 現在の検索条件
  const kasidamaCurrentParams = yield* customSelect(
    kiSyokenKasidamaSearchParamsSelector
  );
  // 変更後の種別で再取得
  const paramsWithShu = {
    ...kasidamaCurrentParams,
    ...selectShu2SearchCondition(action.payload.currentShu),
  };
  const finalParams = yield* buildKasidamaParamsForApi(paramsWithShu);
  yield fork(fetchDataKiSyokenKasidamaSaga, finalParams);
}

/**
 * 表示項目変更時のデータ取得
 */
function* changeKiSyokenKasidamaFieldsSagas(
  action: ChangeKiSyokenKasidamaFieldsAction
) {
  const fields = action.payload.fields.map((item) => item.code);
  const fieldsOrder = action.payload.fieldsOrder;

  // 現在の検索条件
  const kasidamaCurrentParams = yield* customSelect(
    kiSyokenKasidamaSearchParamsSelector
  );

  // 変更後の表示項目で再取得
  const paramsWithUpdatedFields = {
    ...kasidamaCurrentParams,
    fields: fields,
  };
  const finalParams = yield* buildKasidamaParamsForApi(paramsWithUpdatedFields);

  if (fieldsOrder != null && fieldsOrder.length > 0) {
    const currentOrder = yield* customSelect(
      kiSyokenKasidamaColumnsOrderSelector
    );
    const swappedData = yield* customSelect(dataColumnsSwappedSelector);
    if (swappedData == null) {
      throw new Error('data is not found');
    }
    const baseOrder =
      currentOrder.length > 0
        ? currentOrder
        : swappedData.data.columns
            .map((column) => column.code)
            .filter((code): code is string => code != null);
    const newOrder = buildColumnsOrderByPatternOrder(fieldsOrder, baseOrder, {
      fixedLeftCount: 2,
    });
    yield put(
      KiSyokenSettingActionCreators.selectKiSyokenKasidamaColumnsOrderAction(
        newOrder
      )
    );
    yield fork(fetchDataKiSyokenKasidamaSaga, finalParams);
    return;
  }

  yield fork(fetchDataKiSyokenKasidamaSaga, finalParams);

  // 新規追加された表示項目はスティッキー項目を除いて一律前に追加する処理
  const swappedData = yield* customSelect(dataColumnsSwappedSelector);
  const allFields = yield* customSelect(
    settingsOptionsKiSyokenKasidamaFieldsSelector
  );
  if (swappedData == null) {
    throw new Error('data is not found');
  }
  if (allFields == null) {
    throw new Error('settingsOptions is not found');
  }

  const columnsOrder = swappedData.data.columns
    .map((column) => column.code)
    .filter((code) => code != null) as string[];
  const difference =
    fields?.filter((field) => !columnsOrder.includes(field)) ?? [];

  const desiredOrder = allFields.map(({ code }) => code);
  const sortedDifference = [...difference].sort((a, b) => {
    if (desiredOrder == null) {
      return 0;
    }
    return desiredOrder.indexOf(a) < desiredOrder.indexOf(b) ? -1 : 1;
  });

  // 種別とホール名はコンポーネント側でswapを制限しているため常に先頭に存在する
  const shu = columnsOrder.at(0);
  const hlMei = columnsOrder.at(1);
  //fieldsが[]の時にデフォルトの表示項目が表示されるよう、空の配列を入れる
  const resultOrder =
    fields.length && shu != null && hlMei != null
      ? [shu, hlMei, ...sortedDifference, ...columnsOrder.slice(2)]
      : [];

  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenKasidamaColumnsOrderAction(
      resultOrder
    )
  );
}

/**
 * ソートボタン押下時のデータ取得
 */
function* changeKiSyokenKasidamaSortSagas(
  action: ChangeKiSyokenKasidamaSortAction
) {
  // 現在の検索条件
  const searchParams = yield* customSelect(
    kiSyokenKasidamaSearchParamsSelector
  );

  // 変更後のソートで再取得
  const base = {
    ...searchParams,
    sort: action.payload.sort,
    order: action.payload.order,
  };
  const finalParams = yield* buildKasidamaParamsForApi(base);
  yield fork(fetchDataKiSyokenKasidamaSaga, finalParams);
}

/**
 * 表示項目の並び順変更時のステート更新（データ取得はしない）
 */
function* triggerKiSyokenKasidamaSwapFieldsSaga(
  action: TriggerKiSyokenKasidamaSwapFieldsAction
) {
  const { draggedId, droppedId } = action.payload;

  const responseData = yield* customSelect(dataColumnsSwappedSelector);
  if (responseData == null) {
    return;
  }

  const currentOrder = yield* customSelect(
    kiSyokenKasidamaColumnsOrderSelector
  );

  const ordered = recalcColumnOrder(
    currentOrder.length === 0
      ? responseData.data.columns
      : currentOrder.map((code) => ({ code })),
    draggedId,
    droppedId
  );

  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenKasidamaColumnsOrderAction(
      ordered
    )
  );
}

function* handleSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.SEARCH_KISYOKEN_KASIDAMA,
    searchKiSyokenKasidamaDataSaga
  );

  yield takeEvery(
    KiSyokenSettingActionTypes.CHANGE_KISYOKEN_KASIDAMA_SHU,
    changeKiSyokenKasidamaShuSagas
  );

  yield takeEvery(
    KiSyokenSettingActionTypes.CHANGE_KISYOKEN_KASIDAMA_FIELDS,
    changeKiSyokenKasidamaFieldsSagas
  );

  yield takeEvery(
    KiSyokenSettingActionTypes.CHANGE_KISYOKEN_KASIDAMA_SORT,
    changeKiSyokenKasidamaSortSagas
  );

  yield takeEvery(
    KiSyokenSettingActionTypes.TRIGGER_KISYOKEN_KASIDAMA_SWAP_FIELDS,
    triggerKiSyokenKasidamaSwapFieldsSaga
  );
}

export function* kiSyokenKasidamaSaga() {
  yield fork(handleSaga);
}
